import React, { useMemo } from 'react';

import classnames from 'classnames';
import get from 'lodash/get';

import Image, { TRANSFORMATIONS } from 'components/uiLibrary/Image';
import SelectDropdown from 'components/uiLibrary/Inputs/SelectDropdown';
import Skeleton from 'components/uiLibrary/Loaders/Skeleton';
import Typography from 'components/uiLibrary/Typography';

import InformationReferences from 'components/Globals/Display/InformationReferences';
import EntityName from 'components/Globals/EntityName';
import ParseHtml from 'components/ParseHtml';
import { BiographyEmploymentTimeline } from 'components/Artist/Display/EmploymentTimeline';

import { INSTRUMENTALIST_PROFESSION_TYPE_ID, SINGER_PROFESSION_TYPE_ID } from 'constants/consts';
import { TP } from 'constants/index';

import { profileDateConversion } from 'utils/common';
import useBioDescriptions from 'utils/hooks/useBioDescriptions';
import { getMediaSource } from 'utils/media';
import usePrivacyAttributes from 'utils/privacy';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Globals/queries';

import { useTranslation } from 'src/i18n';

import SectionBlock from '../SectionBlock';

import styles from './EntityAbout.module.scss';

// TODO: Complete revamped need to be done on this component as is has same features as AboutOrg
const EntityAbout = ({ entityType, entity, marginBottomNone, sectionProps = {}, trackingData, isOverview = false }) => {
  const { t } = useTranslation('NS_ENTITY_STUB_PAGE');

  const { data: bioData, isFetching } = useQuery(
    queries.getEntityBioDetails({
      entityType,
      entityId: entity?.id,
      queryConfig: {
        enabled: entity?.stats?.about?.exists,
      },
    }),
  );
  const { onChangeLang, references, chosenLang, bioContent, langOptions, isLoading, language } = useBioDescriptions({
    bioData,
    isFetching,
  });

  const profilePictureUrl = getMediaSource({ file: { urls: entity?.image } });

  return (
    <SectionBlock
      title={t(`${TP}.m_ARTIST_BIOGRAPHY`)}
      {...(bioContent && {
        titleChildren:
          langOptions?.length > 1 ? (
            <div className={styles.langDropdownLarge}>
              <Typography size={12}>{t(`${TP}.FN_AVAILABLE_IN`)}</Typography>
              <SelectDropdown
                styles={{
                  select: styles.select,
                  formControl: styles.formControl,
                }}
                options={langOptions}
                value={chosenLang?.value}
                onChange={onChangeLang}
                hideBorder
              />
            </div>
          ) : (
            <div className={styles.singleLanguage}>
              <Typography size={12}>{t(`${TP}.FN_AVAILABLE_IN`)}</Typography>
              <Typography size={12} className={styles.singleLanguage__name}>
                {chosenLang?.language?.name}
              </Typography>
            </div>
          ),
      })}
      styles={{
        root: classnames(styles.root, { [styles.marginBottomNone]: marginBottomNone }),
      }}
      {...sectionProps}
    >
      <div className={styles.content}>
        {isLoading ? (
          <Skeleton variant="rectangular" height={300} />
        ) : (
          <>
            <div className={classnames({ [styles.profileAndBasicInfoOverview]: !!isOverview })}>
              {profilePictureUrl && (
                <div className={classnames(styles.profileImage, { [styles.profileImage__overview]: !!isOverview })}>
                  <Image
                    src={profilePictureUrl}
                    transformations={TRANSFORMATIONS.PROFILE_IMAGE}
                    alt={get(entity, 'name', 'profile photo')}
                  />
                </div>
              )}
              <EntityBasicInfo entity={entity} lang={language} entityType={entityType} trackingData={trackingData} />
            </div>
            {bioContent && (
              <div className={styles.bio}>
                <ParseHtml lang={language}>{bioContent}</ParseHtml>
              </div>
            )}
            <InformationReferences references={references} />
          </>
        )}
      </div>
    </SectionBlock>
  );
};

const EntityBasicInfo = ({ entity, lang, entityType, trackingData }) => {
  const { t } = useTranslation('NS_ENTITY_STUB_PAGE');
  const { canSee } = usePrivacyAttributes();
  const profession = useMemo(
    () =>
      entity?.professions
        ?.filter(prof => prof?.displayStatus === 2)
        ?.map(prof => {
          const profName = prof?.profession?.name;
          if (prof?.creditCount) {
            const creditText = prof.creditCount === 1 ? t('operabase.general.FN_CREDIT') : t('operabase.general.FN_CREDIT_PLURAL');
            return `${profName} (${prof.creditCount} ${creditText})`;
          }
          return profName;
        })
        ?.join(', ') || '',
    [entity],
  );

  const workTypes = useMemo(
    () =>
      entity?.workTypes
        ?.filter(wt => wt?.displayStatus === 2)
        ?.map(wt => {
          const workTypeName = wt?.workType?.name || wt?.label || wt?.name;
          if (wt?.credit_count) {
            const count = wt?.credit_count;
            const creditText = count === 1 ? 'credit' : 'credits';
            return `${workTypeName} (${count} ${creditText})`;
          }
          return workTypeName;
        })
        ?.join(', ') || '',
    [entity?.workTypes],
  );



  const { name, personal, stageName } = entity;
  const birthVisibility = canSee(personal?.birth?.privacyAttributes);
  const deathVisibility = canSee(personal?.death?.privacyAttributes);
  const citizenShipVisibility = canSee(personal?.citizenship?.privacyAttributes);

  const citizenShip = get(personal, 'citizenship', null);
  const [birthDate, deathDate] = useMemo(
    () =>
      profileDateConversion(personal, lang, {
        birthDate: 'birth.date',
        birthYear: 'birth.year',
        birthMonth: 'birth.month',
        birthDay: 'birth.day',
        birthCountry: 'birth.city',
        birthCity: 'birth.country',
        deathDate: 'death.date',
        deathYear: 'death.year',
        deathMonth: 'death.month',
        deathDay: 'death.day',
        deathCountry: 'death.city',
        deathCity: 'death.country',
      })?.split(' - ') || [],
    [lang, personal],
  );

  return (
    <div className={styles.basicDetails}>
      {name && (
        <Typography color="secondary">
          {t(`${TP}.FULL_NAME`)}{' '}
          <Typography weight="bold">
            : <EntityName entityType={entityType} entity={entity} trackingData={trackingData} isRaw />
          </Typography>
        </Typography>
      )}
      {stageName && (
        <Typography variant="p" color="secondary">
          {t(`${TP}.FN_STAGE_PROFESSIONAL_NAME`)}{' '}
          <Typography weight="bold">
            : <EntityName name={stageName} entityType={entityType} entity={entity} trackingData={trackingData} isRaw />
          </Typography>
        </Typography>
      )}
      {entity?.alias && (
        <Typography variant="p" color="secondary">
          {t(`${TP}.FN_ALIAS_NAME_DISPLAY`)} <Typography weight="bold">{`: ${entity?.alias}`}</Typography>
        </Typography>
      )}
      {profession && (
        <Typography variant="p" color="secondary">
          {t(`${TP}.FN_PROFESSION`)} <Typography weight="bold">{`: ${profession}`}</Typography>
        </Typography>
      )}
      {workTypes && (
        <Typography variant="p" color="secondary">
          {t(`${TP}.FN_GENRE`)} <Typography weight="bold">{`: ${workTypes}`}</Typography>
        </Typography>
      )}

      {citizenShipVisibility && citizenShip?.countries && citizenShip?.countries.length > 0 && (
        <Typography variant="p" color="secondary">
          {`${t(`${TP}.FN_NATIONALITY_LABEL`)}: `}
          <Typography weight="bold">{citizenShip?.countries?.map(c => c?.name).join(', ')}</Typography>
        </Typography>
      )}
      {birthVisibility && birthDate && (
        <div>
          <Typography variant="p" color="secondary">
            {`${t(`${TP}.BORN`)}: `} <Typography weight="bold">{birthDate}</Typography>
          </Typography>
        </div>
      )}
      {deathVisibility && deathDate && (
        <div>
          <Typography variant="p" color="secondary">
            {`${t(`${TP}.DIED`)}: `} <Typography weight="bold">{deathDate}</Typography>
          </Typography>
        </div>
      )}

      {/* Employment Timeline Section */}
      {entity?.organizations && entity.organizations.length > 0 && (
        <BiographyEmploymentTimeline 
          organizations={entity.organizations}
          entityType={entityType}
          className={styles.employmentTimeline}
        />
      )}
    </div>
  );
};

export default EntityAbout;
